import { ICalloutContentStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTarget } from 'baseComponents/Utils/Target';
import ClassificationList from 'components/Classification/ClassificationList';
import HrefLink from 'components/Links/HrefLink';
import Classification from 'models/classification/classification';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IClassificationPickerProps {
  isOpen: boolean;
  target: IPTarget;
  onSelect: (item: Classification[]) => void;
  onDismiss: () => void;
  hideGroups?: number[];
  preSelectedClassifications?: Classification[];
  selectMultiple?: boolean;
  allowMultiplePerGroup?: boolean;
}

const ClassificationPicker = (props: IClassificationPickerProps) => {
  const { t } = useTranslation(['translation', 'classifications']);
  const appContext = useContext(AppContext);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedClassifications, setSelectedClassifications] = useState<Classification[] | undefined>(undefined);

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      //classifications
      let classifications = (await appContext.globalDataCache.classifications.getItems()).map((classification) =>
        classification.clone(),
      );
      if (props.hideGroups) {
        classifications = classifications.filter((c) => !props.hideGroups?.includes(c.group.stringValueId));
      }
      setClassifications(classifications);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: '10px 12px',
      minHeight: 300,
      height: '50vh',
      maxHeight: '80vh',
      minWidth: 300,
      width: '50vw',
      maxWidth: 1200,
    },
  };

  //
  // Main render
  //
  return (
    <IPCallout
      styles={calloutStyle}
      hidden={!props.isOpen}
      setInitialFocus
      gapSpace={0}
      target={props.target}
      onDismiss={() => props.onDismiss()}
      dismissOnTargetClick
    >
      <IPStack verticalFill>
        {!isLoading && classifications.length === 0 && (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPText>{t('translation:General.Notifications.NoItems')}</IPText>
            <HrefLink href="/admin/settings/modules#classifications">
              {t('classifications:ClassificationLink')}
            </HrefLink>
          </IPStack>
        )}
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && classifications.length > 0 && (
          <IPStack.Item grow>
            <ClassificationList
              classifications={classifications}
              onSelectMultipleClassifications={
                props.selectMultiple
                  ? (item) => {
                      setSelectedClassifications(item);
                    }
                  : undefined
              }
              onSelectClassification={
                !props.selectMultiple
                  ? (item) => {
                      setSelectedClassifications([item]);
                    }
                  : undefined
              }
              isLoading={isLoading}
              showSearch={true}
              preSelectedClassifications={props.preSelectedClassifications}
              selectMultiple={props.selectMultiple}
              allowMultiplePerGroup={props.allowMultiplePerGroup}
            />
          </IPStack.Item>
        )}
        <IPSeparator />
        <IPStack.Item>
          <IPPrimaryButton
            disabled={selectedClassifications === undefined}
            onClick={() => {
              if (selectedClassifications) {
                props.onSelect(selectedClassifications);
              }
            }}
          >
            {t('translation:General.Button.Select')}
          </IPPrimaryButton>
        </IPStack.Item>
      </IPStack>
    </IPCallout>
  );
};

export default ClassificationPicker;
